import React, { useEffect, useRef, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { AI_AGENTS, useSiteAgent } from '../cms/AskYourAgent';
import { showInkToast } from '../cms/InkToast';

/**
 * Article → "Ask agent ⌘J" (Prototype v14): a box at the bottom of the article
 * for asking the site's agent to change it. The agent only acts when it's used
 * (the person talks to it in their AI app), so the request waits for it; its
 * edit comes back as a proposal for approval and is never applied directly.
 * Below the box, this article's requests: waiting ones can be withdrawn.
 */

export const AGENT_REQUESTS = gql`
  query AgentRequests($accountId: ID!, $articleId: ID!) {
    agentRequests(accountId: $accountId, articleId: $articleId) {
      id
      request
      status
      createdAt
      askedBy
    }
  }
`;
const ASK_AGENT = gql`
  mutation AskAgent($accountId: ID!, $articleId: ID!, $request: String!) {
    askAgent(accountId: $accountId, articleId: $articleId, request: $request) {
      id
    }
  }
`;
const CANCEL_AGENT_REQUEST = gql`
  mutation CancelAgentRequest($accountId: ID!, $id: ID!) {
    cancelAgentRequest(accountId: $accountId, id: $id)
  }
`;

interface AgentRequest {
  id: string;
  request: string;
  status: 'open' | 'answered' | 'cancelled' | string;
  createdAt: string;
  askedBy?: string | null;
}

export const agentInitials = (name: string) =>
  name === 'Your agent'
    ? '✦'
    : name
        .split(/\s+/)
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

const when = (v: string) => {
  const d = new Date(/^\d{10,}$/.test(v) ? Number(v) : v);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export function AskAgent({
  accountId,
  articleId,
  agentName,
  onClose,
  changes = false,
}: {
  accountId: string;
  articleId: string;
  agentName: string;
  onClose: () => void;
  /** Opened from Request changes: asks what to change in the version waiting. */
  changes?: boolean;
}) {
  // "your agent" mid-sentence when the connector has no name of its own.
  const named = agentName === 'Your agent' ? 'your agent' : agentName;
  const prompt = changes
    ? `What should ${named} change before you approve it?`
    : `Ask ${named} to update this article`;
  const [text, setText] = useState('');
  const { connected } = useSiteAgent();
  const inputRef = useRef<HTMLInputElement>(null);
  const { data, refetch } = useQuery<{ agentRequests: AgentRequest[] }>(AGENT_REQUESTS, {
    variables: { accountId, articleId },
    fetchPolicy: 'cache-and-network',
  });
  const [ask, { loading: asking }] = useMutation(ASK_AGENT);
  const [cancel] = useMutation(CANCEL_AGENT_REQUEST);
  const requests = (data?.agentRequests || []).filter((r) => r.status !== 'cancelled');

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const send = async () => {
    const request = text.trim();
    if (!request || asking) return;
    try {
      await ask({ variables: { accountId, articleId, request } });
      setText('');
      await refetch();
      showInkToast(`Sent to ${named}. It picks this up the next time you use it.`);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t send.');
    }
  };

  const withdraw = async (id: string) => {
    try {
      await cancel({ variables: { accountId, id } });
      await refetch();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };

  return (
    <div className="ink-askagent" role="region" aria-label={`Ask ${named}`}>
      {requests.length ? (
        <div className="ink-askagent-list">
          {requests.slice(0, 4).map((r) => (
            <div key={r.id} className="ink-askagent-req" data-status={r.status}>
              <span className="ink-askagent-req-text">{r.request}</span>
              <span className="ink-muted">
                {r.status === 'open'
                  ? `Waiting for ${named} · asked ${when(r.createdAt)}`
                  : `${agentName} saved a new version · review it in the panel`}
              </span>
              {r.status === 'open' ? (
                <button
                  type="button"
                  className="ink-quiet-link"
                  onClick={() => void withdraw(r.id)}
                >
                  Cancel
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      <form
        className="ink-askagent-box"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <span className="ink-askagent-mark" aria-hidden="true">
          {agentInitials(agentName)}
        </span>
        <input
          ref={inputRef}
          value={text}
          maxLength={2000}
          placeholder={prompt}
          aria-label={prompt}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
          }}
        />
        {text.trim() ? (
          <button type="submit" className="ink-btn ink-btn-primary" disabled={asking}>
            {asking ? 'Sending…' : 'Send'}
          </button>
        ) : null}
        <button type="button" className="ink-quiet-link" onClick={onClose}>
          Close
        </button>
      </form>
      <div className="ink-askagent-note">
        {connected === false ? (
          <>
            No agent is connected to this site yet, so nothing picks this up.{' '}
            <Link to={AI_AGENTS}>Connect an agent</Link>
          </>
        ) : (
          <>
            {agentName} sees this the next time you use it. Changes it makes come back to you as a
            proposal; they are never applied directly.
          </>
        )}
      </div>
    </div>
  );
}
